import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CoverLettersApiService } from "@shared/services/cover-letters-api.service";
import { CoverLetter, CoverLetterCreateModel } from "@models/documents/cover-letter";
import { toBase64 } from "../../../../../../shared/utils/to-base64";
import { bytesToMegabytes } from "../../../../../../shared/utils/bytes-to-megabytes";
import {
  FileCreateDtoWithSize
} from "@shared/components/notes/components/create-edit-note-control/create-edit-note-control.component";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { downloadBase64File } from "../../../../../../shared/utils/download-base64-file";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputComponent } from "@shared/controls/input/input.component";
import { finalize } from "rxjs";
import { setControlDisabled } from "../../../../../../shared/utils/set-control-disabled";
import { FileUploadComponent } from "@shared/controls/file-upload/file-upload.component";
import { CustomFormValidators } from "../../../../../../shared/validators/custom-form-validators";
import { ToastrService } from "ngx-toastr";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { AcceptedFileFormat } from "@shared/components/documents-related/annual-upload-files-modal/upload-files-modal.component";

@Component({
  selector: 'app-add-document-template-modal',
  standalone: true,
  providers: [CoverLettersApiService],
  imports: [
    ReactiveFormsModule,
    CommonModalFormComponent,
    InputComponent,
    FileLabelComponent,
    FileUploadComponent,
    DisclaimerComponent
  ],
  templateUrl: './add-document-template-modal.component.html',
  styleUrls: [
    './add-document-template-modal.component.scss',
    '../../../manage-tags-page/manage-tags-modal/manage-tags-modal.component.scss'
  ]
})
export class AddDocumentTemplateModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private coverLetterService = inject(CoverLettersApiService);
  private toastr = inject(ToastrService);

  @ViewChild('fileUploadInput') fileUploadInput!: ElementRef<HTMLInputElement>;

  @Input() editedCoverLetter: CoverLetter | null = null;

  readonly allowedMaxFileSizeMB = 5;
  readonly acceptedFormatsList: AcceptedFileFormat[] = ['.docx'];
  readonly acceptedFormats = this.acceptedFormatsList.join(', ');
  readonly deleteConfirmMessage = 'Deleting will permanently remove this document template. Do you want to proceed?';
  readonly titleControlCustomErrors = { maxLength: 'You\'ve exceeded the 100-character limit' };

  subheader = 'Cover Letter Template';
  isLoading = false;
  errorMessage = '';
  fileToUpload: FileCreateDtoWithSize | null = null;
  titleFormControl = new FormControl('', [Validators.required, CustomFormValidators.maxLength(100)]);

  ngOnInit(): void {
    this.activeModal.update({ modalDialogClass: 'common-modal-form-dialog' });
    this.subheader = (this.editedCoverLetter ? 'Edit ' : 'New ') + this.subheader;

    if (this.editedCoverLetter) {
      this.titleFormControl.setValue(this.editedCoverLetter.title);
      this.fileToUpload = {
        fileName: this.editedCoverLetter.fileName,
        base64FileContent: '',
        size: 0
      };
    }
  }

  confirm(): void {
    if (this.titleFormControl.invalid) {
      this.titleFormControl.markAsTouched();
      return;
    }

    if (!this.fileToUpload) {
      this.setErrorMessage('Please select a file to upload.');
      return;
    }

    if (this.editedCoverLetter) {
      this.patchTemplate();
    } else {
      this.createNewCoverLetter();
    }
  }

  async onFilesLoaded(files: File[]): Promise<void> {
    const file = files[0];

    if (!this.acceptedFormatsList.some((fileExtension) => file.name.endsWith(fileExtension))) {
      this.setErrorMessage(`Uploaded file has not allowed file extension. Please use one of allowed file extensions: ${ this.acceptedFormats }`);
      return;
    }

    if (bytesToMegabytes(file.size) > this.allowedMaxFileSizeMB) {
      this.setErrorMessage(`Uploaded file has not allowed file is too big. Max file size is ${ this.allowedMaxFileSizeMB } MB`);
      return;
    }

    this.fileToUpload = {
      fileName: file.name,
      base64FileContent: await toBase64(file),
      size: bytesToMegabytes(file.size)
    };

    this.setErrorMessage();
  }

  downloadFile(): void {
    if (this.editedCoverLetter) {
      this.setIsLoading(true);
      this.coverLetterService.downloadCoverLetterTemplate(this.editedCoverLetter.id, this.editedCoverLetter.fileName)
        .pipe(finalize(() => this.setIsLoading(false)))
        .subscribe((file) => downloadBase64File(file, this.editedCoverLetter!.fileName));
    } else if (!this.editedCoverLetter && this.fileToUpload?.base64FileContent)
      downloadBase64File(this.fileToUpload.base64FileContent, this.fileToUpload.fileName);
  }

  close(): void {
    this.activeModal.dismiss();
  }

  setErrorMessage(errorMessage = ''): void {
    this.errorMessage = errorMessage;
  }

  deleteTemplate(): void {
    if (this.editedCoverLetter && confirm(this.deleteConfirmMessage)) {
      this.coverLetterService.deleteCoverLetterTemplate(this.editedCoverLetter.id)
        .subscribe({
          next: () => this.activeModal.close(),
          error: (err) => console.warn('[deleteTemplate] ', err)
        });
    }
  }

  private patchTemplate(): void {
    this.coverLetterService.patchCoverLetter(new CoverLetter({
      ...this.editedCoverLetter,
      title: this.titleFormControl.value!,
    })).subscribe({
      next: () => {
        this.activeModal.close();
        this.toastr.success('Data saved successfully', 'Success');
      },
      error: (err) => console.warn('[patchTitle] ', err)
    });
  }

  private createNewCoverLetter(): void {
    const coverLetterCreateModel = new CoverLetterCreateModel({
      title: this.titleFormControl.value!,
      file: this.fileToUpload!,
    });

    this.setIsLoading(true);
    this.coverLetterService.createCoverLetterTemplate(coverLetterCreateModel)
      .subscribe({
        next: () => {
          this.activeModal.close();
          this.toastr.success('Data saved successfully', 'Success');
        },
        error: (err) => {
          const message = err.status === 400
            ? err.error.message
            : 'Couldn\'t create the Cover Letter';

          this.toastr.error(message, 'Error');
          console.warn('[createNewCoverLetter] ', err);
        },
        complete: () => {
          this.setIsLoading(false);
        },
      });
  }

  private setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
    setControlDisabled(this.titleFormControl, isLoading);
  }
}
